export const scrollToElement = (_id, params = {}) =>
  document.getElementById(_id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    ...params
  });

export function getLastRefreshLabel(timestamp) {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let label = '';

  if (seconds < 60) {
    label = 'just now';
  } else if (minutes < 60) {
    label = `${minutes}min ago`;
  } else if (hours < 24) {
    label = `${hours}hr ago`;
  } else {
    label = `${days}d ago`;
  }

  return `Last refresh ${label}`;
}

export const isOxFlowLocation = () =>
  typeof window !== 'undefined' && window.location.origin.includes('ox-flow');

export const getYearsForLastFiveYears = () => {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
};

const ERROR_MESSAGE_LABELS = {
  filing_type: 'Filing Type'
};

const getValidationDetails = err => {
  const data = err?.response?.data || err?.data;

  if (Array.isArray(data?.details) && data.details.length > 0) return data.details;
  if (Array.isArray(data?.detail) && data.detail.length > 0) return data.detail;

  return null;
};

const getDetailString = err => {
  const data = err?.response?.data || err?.data;

  if (typeof data?.details === 'string' && data.details) {
    return data.details;
  }

  if (typeof data?.detail === 'string' && data.detail) {
    return data.detail;
  }

  return null;
};

const formatValidationMessage = detail => {
  if (!detail?.msg) return null;

  const fieldPart =
    detail.loc?.length > 1
      ? ` (${detail.loc
          .slice(1)
          .map(loc => ERROR_MESSAGE_LABELS[loc] || loc)
          .join('.')})`
      : '';

  return `${detail.msg}${fieldPart}`;
};

export const getErrorMessage = err => {
  const details = getValidationDetails(err);

  if (details) {
    let _message = '';

    details.forEach(item => {
      _message += `${item?.msg}. `;

      if (item.loc?.length > 1) {
        _message += `Field: ${item.loc
          .slice(1)
          .map(loc => ERROR_MESSAGE_LABELS[loc] || loc)
          .join(', ')}. `;
      }
    });

    return _message;
  }

  return extractApiError(err, 'An unexpected error occurred');
};

/**
 * Extracts a user-friendly error message from any error shape:
 * - Axios response errors  ({ data: { detail, message } })
 * - Standard JS errors     ({ message })
 * - Plain strings
 * Truncates to 200 chars so toasts stay readable.
 */
export const extractApiError = (err, fallback = 'Something went wrong. Please try again.') => {
  if (!err) return fallback;

  const data = err?.response?.data || err?.data;

  if (err?.code === 'ECONNABORTED') {
    return 'The request timed out. Please try again.';
  }

  if (err?.code === 'ERR_NETWORK' || (err?.request && !err?.response)) {
    return 'The server did not return a response. Please try again or contact support.';
  }

  const validationDetails = getValidationDetails(err);
  if (validationDetails) {
    const formatted = formatValidationMessage(validationDetails[0]);
    if (formatted) return formatted.slice(0, 200);
  }

  const technicalDetails = getDetailString(err);
  if (technicalDetails) {
    return technicalDetails.split('\n')[0].slice(0, 200);
  }

  // New structured envelope: { message, error_code }
  const structuredMessage = data?.message;
  if (typeof structuredMessage === 'string' && structuredMessage) {
    return structuredMessage.slice(0, 200);
  }

  // JS Error object
  if (typeof err?.message === 'string' && err.message) {
    return err.message.slice(0, 200);
  }

  // Plain string passed directly
  if (typeof err === 'string') return err.slice(0, 200);

  return fallback;
};
